import type { Permission } from '../../../types/User';
import { uiDash } from '../../../utils/uiText';

/**
 * Display helpers for the Roles screens (W-M12, W-L1).
 *
 * Permission names are UI labels, not business data, but they live in Permission rows that the
 * bootstrap only creates when missing - so a database seeded with "Create Submitted time Report"
 * keeps that text. They are title-cased here at render time; the stored row is never touched.
 */

/** One name for the widget category everywhere (Create/Edit Role and Show Details). */
export const WIDGETS_MODULE = 'Widgets';

/** Show Details group for access keys with no Permission row - kept visible, listed last. */
export const LEGACY_CODES_MODULE = 'Legacy Codes';

export const TRUECONTEXT_MODULE = 'TrueContext Configuration';

const SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'or', 'per', 'the', 'to', 'vs', 'via', 'with',
]);

function capitalise(word: string): string {
  // Leave words that already carry capitals alone: KPI, PCR, IDs, TrueContext, DRM.
  if (/[A-Z]/.test(word)) return word;
  const i = word.search(/[a-z]/);
  if (i < 0) return word;
  return word.slice(0, i) + word.charAt(i).toUpperCase() + word.slice(i + 1);
}

/** "Create Submitted time Report" → "Create Submitted Time Report"; "notes/history" → "Notes/History". */
export function toTitleCase(text: string): string {
  const words = text.split(/(\s+)/);
  let index = 0;
  return words
    .map((part) => {
      if (/^\s+$/.test(part) || part === '') return part;
      const first = index === 0;
      index += 1;
      const lower = part.toLowerCase();
      if (!first && SMALL_WORDS.has(lower) && part === lower) return part;
      return part
        .split(/([/-])/)
        .map((seg) => (seg === '/' || seg === '-' ? seg : capitalise(seg)))
        .join('');
    })
    .join('');
}

/** The label a permission is shown with: Title Case, en dashes. */
export function permissionLabel(name: string): string {
  return toTitleCase(uiDash(name));
}

/** "1 user", "2 users", "0 permissions". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

const collator = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });

/** Permissions A–Z by the label the user reads. */
export function sortPermissions<T extends Pick<Permission, 'name'>>(perms: T[]): T[] {
  return [...perms].sort((a, b) => collator.compare(permissionLabel(a.name), permissionLabel(b.name)));
}

/**
 * Category names A–Z. The legacy-codes group always goes last so it does not sit between real
 * categories, but it is never dropped.
 */
export function sortModuleNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    if (a === LEGACY_CODES_MODULE) return 1;
    if (b === LEGACY_CODES_MODULE) return -1;
    return collator.compare(a, b);
  });
}

/** Group, then sort categories and the permissions within each, A–Z. */
export function groupAndSortPermissions<T extends Pick<Permission, 'name'>>(
  perms: T[],
  moduleOf: (p: T) => string,
): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const p of perms) {
    const m = moduleOf(p);
    const list = groups.get(m);
    if (list) list.push(p);
    else groups.set(m, [p]);
  }
  return sortModuleNames(Array.from(groups.keys())).map((m) => [m, sortPermissions(groups.get(m)!)]);
}

const LEGACY_WORDS: Record<string, string> = {
  dailylog: 'Daily Log',
  he: 'HE',
  hemaster: 'Hired Equipment',
  pcr: 'PCR',
  po: 'PO',
  qp: 'QP',
  wars: 'WARS',
  chris: 'CHRIS',
  mott: 'MOTT',
  ot: 'OT',
  rb: 'RB',
  id: 'ID',
  ids: 'IDs',
};

function legacyWords(part: string): string {
  if (LEGACY_WORDS[part.toLowerCase()]) return LEGACY_WORDS[part.toLowerCase()];
  return part
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_\s.-]+/)
    .filter(Boolean)
    .map((w) => LEGACY_WORDS[w.toLowerCase()] ?? w)
    .join(' ');
}

/**
 * V2-066 / LANG-06: a legacy access code shown in words, never as the raw key.
 * "submission:dailylog_approve" → "Submission – Daily Log Approve".
 */
export function legacyCodeLabel(key: string): string {
  const [area, ...rest] = key.split(':');
  const action = rest.join(' ');
  const label = action ? `${legacyWords(area)} – ${legacyWords(action)}` : legacyWords(area);
  return toTitleCase(label);
}
